/**
 * BlockConverterService
 *
 * Sprint: sprint-4, Task: T005.2.2
 * Requirement: FR-P3-003 - Convert between Firebase StrategyBlock and Canvas Block formats
 *
 * Converts between Firebase StrategyBlock entities and ReactFlow Canvas Block types.
 *
 * KEY SIMPLIFICATIONS vs. ScreenConverterService:
 * - No ScreenHandleResolver needed (blocks have uniform structure)
 * - No complex screenType mapping (blocks use blockType directly)
 * - Single action.edges[] location (vs. multiple struct locations for screens)
 * - Fixed handle positions (blocks are simpler than screens)
 *
 * Usage:
 * ```typescript
 * import { blockConverter } from '@modules/reactFlow/utils/converters/BlockConverterService';
 *
 * // Convert Firebase blocks to canvas format
 * const canvasBlocks = blockConverter.toCanvasBlocks(firebaseBlocks);
 *
 * // Convert canvas block back to Firebase format
 * const fbBlock = blockConverter.toFirebaseBlock(canvasBlock);
 * ```
 */

import type { StrategyBlock } from '@segmently/schema-models/strategy';
import { TransitionIdGenerator } from '@modules/reactFlow/utils/converters/TransitionIdGenerator';
import { BLOCK_ACTION_KEY } from '@modules/reactFlow/constants/actionKeys';
import { BLOCK_HANDLE_REGIONS } from '@modules/reactFlow/constants/blockDimensionConfig';
import type { ConnectionHandle } from '@modules/reactFlow/types/flow-canvas-types';
import type { EntityTransition } from '@modules/reactFlow/types/EntityTransition';
import type { EntityConnection } from '@modules/reactFlow/types/EntityConnection';

/**
 * Canvas Block representation (for ReactFlow)
 *
 * Simplified vs. CanvasScreen - blocks have uniform structure
 */
export interface CanvasBlock {
  id: string;
  name: string;
  type: string; // Always 'strategy-block'
  position: { x: number; y: number };
  properties: {
    blockType?: string;
    description?: string;
    order?: number;
    firstScreenId?: string;
  };
  connectionHandles: ConnectionHandle[];
}

/**
 * Service for converting between Firebase and Canvas block formats
 *
 * Much simpler than ScreenConverterService due to uniform block structure
 */
export class BlockConverterService {
  /**
   * Convert Firebase StrategyBlock to Canvas Block format
   *
   * @param firebaseBlock - Block from Firebase/Firestore
   * @returns Canvas-compatible block object
   */
  toCanvasBlock(firebaseBlock: StrategyBlock): CanvasBlock {
    // Validate entityId presence
    if (!firebaseBlock.entityId) {
      console.error(`[BlockConverterService] ❌ CRITICAL: Block ${firebaseBlock.id} missing entityId`);
      throw new Error(`Block ${firebaseBlock.id} missing entityId`);
    }

    // Extract connections from block.action.edges[]
    const connections = this.extractConnections(firebaseBlock);

    return {
      id: firebaseBlock.entityId,
      name: firebaseBlock.name || 'Untitled Block',
      type: 'strategy-block', // Uniform type for all blocks
      position: {
        x: firebaseBlock.position?.x ?? Math.random() * 500,
        y: firebaseBlock.position?.y ?? Math.random() * 500,
      },
      properties: {
        blockType: firebaseBlock.blockType,
        description: firebaseBlock.description,
        order: firebaseBlock.order,
        firstScreenId: firebaseBlock.firstScreenId,
      },
      connectionHandles: connections.map((conn) => ({
        id: TransitionIdGenerator.generateHandleId(firebaseBlock.entityId || firebaseBlock.id, conn.actionKey),
        sourceActionKey: conn.actionKey,
        position: { x: 0, y: conn.yPosition },
        label: conn.label,
        elementType: 'block-transition' as const,
        elementText: conn.label,
      })),
    };
  }

  /**
   * Convert array of Firebase StrategyBlocks to Canvas Blocks
   *
   * @param firebaseBlocks - Array of Firebase blocks
   * @returns Record of canvas blocks by ID
   */
  toCanvasBlocks(firebaseBlocks: StrategyBlock[]): Record<string, CanvasBlock> {
    const canvasBlocks: Record<string, CanvasBlock> = {};

    for (const fbBlock of firebaseBlocks) {
      if (!fbBlock.entityId) {
        console.error(`[BlockConverterService] ❌ CRITICAL: Block ${fbBlock.id} missing entityId - skipping`);
        continue;
      }
      canvasBlocks[fbBlock.entityId] = this.toCanvasBlock(fbBlock);
    }

    return canvasBlocks;
  }

  /**
   * Convert Canvas Block back to Firebase StrategyBlock format
   *
   * @param canvasBlock - Canvas block object
   * @returns Firebase-compatible block object
   */
  toFirebaseBlock(canvasBlock: CanvasBlock): Partial<StrategyBlock> {
    return {
      id: canvasBlock.id,
      name: canvasBlock.name,
      position: {
        x: canvasBlock.position.x,
        y: canvasBlock.position.y,
      },
      blockType: canvasBlock.properties.blockType as any,
      description: canvasBlock.properties.description,
      order: canvasBlock.properties.order,
      firstScreenId: canvasBlock.properties.firstScreenId,
      // Note: action.edges[] not mapped back here
      // (handled by BlockTransitionManager)
    };
  }

  /**
   * Extract connections from Firebase block structure
   *
   * MUCH SIMPLER than ScreenConverterService:
   * - Single location: block.action.edges[]
   * - No ScreenHandleResolver needed
   * - No complex struct traversal
   * - Fixed handle position
   *
   * @param block - Firebase block
   * @returns Array of connections
   */
  private extractConnections(block: StrategyBlock): EntityConnection[] {
    const connections: EntityConnection[] = [];

    // Check block.action.edges[]
    const edges = block.action?.edges;
    if (!edges || !Array.isArray(edges)) {
      return connections;
    }

    // Extract connections from edges
    edges.forEach((edge, index) => {
      if (edge.nextBlockId) {
        connections.push({
          targetEntityId: edge.nextBlockId,
          actionKey: BLOCK_ACTION_KEY, // Uniform action key for all blocks
          label: `Next Block`, // Simple label (could be enhanced later)
          yPosition: BLOCK_HANDLE_REGIONS.DEFAULT_CENTER, // Centered Y position for block handles (316px height / 2 = 158px)
        });
      }
    });

    return connections;
  }

  /**
   * Extract connection handles that SHOULD be displayed on canvas
   *
   * For blocks: Always show single output handle (even without edges)
   * This allows creating new connections from any block
   *
   * @param block - Firebase block
   * @returns Array of handles to display
   */
  extractConnectionHandlesForDisplay(block: StrategyBlock): ConnectionHandle[] {
    const connections = this.extractConnections(block);

    // If block has connections, return those handles
    if (connections.length > 0) {
      return connections.map((conn) => ({
        id: TransitionIdGenerator.generateHandleId(block.entityId || block.id, conn.actionKey),
        sourceActionKey: conn.actionKey,
        position: { x: 0, y: conn.yPosition },
        label: conn.label,
        elementType: 'block-transition',
        elementText: conn.label,
      }));
    }

    // If no connections, still show handle for creating new connections
    return [
      {
        id: TransitionIdGenerator.generateHandleId(block.entityId || block.id, BLOCK_ACTION_KEY),
        sourceActionKey: BLOCK_ACTION_KEY,
        position: { x: 0, y: BLOCK_HANDLE_REGIONS.DEFAULT_CENTER },
        label: 'Next Block',
        elementType: 'block-transition',
        elementText: 'Next Block',
      },
    ];
  }

  /**
   * Extract transitions from array of blocks
   *
   * Returns transitions that have BOTH source and target blocks
   * (filters out orphaned transitions)
   *
   * @param blocks - Array of Firebase blocks
   * @returns Array of transitions with both endpoints valid
   */
  extractTransitions(blocks: StrategyBlock[]): EntityTransition[] {
    const transitions: EntityTransition[] = [];

    const blockIds = new Set(blocks.map(b => b.entityId || b.id).filter((id): id is string => !!id));

    for (const block of blocks) {
      if (!block.entityId) {
        console.error(`[BlockConverterService] ❌ CRITICAL: Block ${block.id} missing entityId - skipping transitions`);
        continue;
      }

      const edges = block.action?.edges;
      if (!edges || !Array.isArray(edges)) continue;

      edges.forEach((edge) => {
        if (edge.nextBlockId && blockIds.has(edge.nextBlockId)) {
          transitions.push({
            id: TransitionIdGenerator.generate(block.entityId || block.id, BLOCK_ACTION_KEY, edge.nextBlockId),
            sourceEntityId: block.entityId || block.id!,  // ✅ EntityTransition property
            targetEntityId: edge.nextBlockId,  // ✅ EntityTransition property
            sourceActionKey: BLOCK_ACTION_KEY,
            label: undefined,  // Blocks don't have labels
          });
        }
      });
    }

    return transitions;
  }
}

// Export singleton instance
export const blockConverter = new BlockConverterService();
